import React, {
  createContext,
  PropsWithChildren,
  useEffect,
  useState,
} from 'react';
import {
  RecommendationsRequest,
  MAPlan,
  MedigapLetterRecommendation,
  useChatAi,
  PageContext,
  ChatAnswer,
  PDPPlan,
  RecommendationsResponse,
  RouteResult,
  TypeSummary,
  SessionInitResponse,
  HighlightsResponse,
} from './api';
import { useMedicareQuote, usePlanEngine } from './api';
import { useUserProfile } from './api/enrollment/use-user-profile';
import { useAppContext } from '@coverright/shared/contexts';

export type MedigapPlanWithPdp = MedigapLetterRecommendation & {
  pdp_plans?: PDPPlan[]
}

type RecommendedPlan = (MAPlan | MedigapPlanWithPdp) & {
  bid_id: string,
  name?: string | null,
}

export const isMAPlan = (plan: RecommendedPlan): plan is MAPlan => 'plan_type' in plan;
export const isMGPlan = (plan: RecommendedPlan): plan is MedigapPlanWithPdp & {bid_id: string} => 'plan_letter' in plan;

type AiContextType = {
  recommendedPlans: RecommendedPlan[],
  recommendations?: RecommendationsResponse,
  highlights?: HighlightsResponse,
  route?: RouteResult,
  loading: boolean,
  suggestedQuestions: string[],
  suggestedType?: RouteResult['suggested_type'],
  updateRecommendations: (options?: Partial<RecommendationsRequest>) => void,
  initChatSession: (context: PageContext) => Promise<SessionInitResponse | boolean>,
  chat: (question: string) => Promise<ChatAnswer>,
}

const AiContext = createContext<AiContextType>({
  recommendedPlans: [],
  loading: false,
  suggestedQuestions: [],
  updateRecommendations: () => {},
  initChatSession: async () => false,
  chat: async () => ({answer: ''}),
})

export const AiProvider = ({children}: PropsWithChildren) => {
  const profile = useUserProfile();
  const quote = useMedicareQuote();
  const [{ hasDrugs, hasDoctors, benefits, situation, paymentPreference, programs, planType }] = useAppContext();
  const hasAnswers = [
    hasDoctors,
    hasDrugs,
    benefits,
    situation,
    programs,
    paymentPreference,
    planType
  ].some((v) => v !== undefined);

  const [loading, setLoading] = useState(true);

  const [highlights, setHighlights] = useState<HighlightsResponse>();
  const [recommendations, setRecommendations] = useState<RecommendationsResponse>();
  const [recommendedPlans, setRecommendedPlans] = useState<RecommendedPlan[]>([]);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [suggestedType, setSuggestedType] = useState<RouteResult['suggested_type']>();
  const planEngineApi = usePlanEngine(quote);
  const chatApi = useChatAi();

  const updateRecommendations = async (options: Partial<RecommendationsRequest> = {}) => {
    setLoading(true);
    const res = await planEngineApi.recommendations(options);

    if (res) {
      setRecommendations(res);
      let recommended: RecommendedPlan[];
      if (hasAnswers) {
        setSuggestedType(res.route.suggested_type);
        recommended =
          res.route.suggested_type === 'ma'
            ? ((res.ma?.recommendations ?? []).slice(0, 3) as RecommendedPlan[])
            : ((res?.medigap?.recommendations ?? []).slice(
                0,
                3
              ) as RecommendedPlan[]);
      } else {
        const maPlans = (res.ma?.recommendations ?? []).slice(0, 2);
        const neededFromMedigap = 3 - maPlans.length;
        const medigapPlans = (res.medigap?.recommendations ?? []).slice(0, neededFromMedigap);
        recommended = [...maPlans, ...medigapPlans] as RecommendedPlan[];
      }

      setRecommendedPlans(recommended.map(p => ({
        ...p,
        bid_id: isMAPlan(p) ? p.bid_id : p.plan_letter,
        name: isMAPlan(p) ? p.plan_name : p.plan_letter,
      })));

      setHighlights(await chatApi.highlights([...(res.ma?.recommendations.map(p => p.bid_id) || []), ...(res.medigap?.recommendations.map(p => p.plan_letter) || [])]));
    }
    setLoading(false);
  }

  useEffect(() => {
    if (profile && quote) updateRecommendations()
  }, [
    // profile / quote identity
    profile,
    quote?.zip,
    // quiz step: doctors
    hasDoctors,
    quote?.preferredDoctors?.length,
    // quiz step: drugs
    hasDrugs,
    quote?.preferredDrugs?.length,
    // quiz step: pharmacy
    quote?.preferredPharmacies?.length,
    // quiz step: special programs (array → stringify to avoid reference churn)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    programs?.join(','),
    // quiz step: situation
    situation,
    // quiz step: payment / plan-type
    paymentPreference,
    planType,
    // quiz step: benefits (array → stringify)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    benefits?.join(','),
  ]);


  const initChatSession = async (context: PageContext): Promise<SessionInitResponse | boolean> => {
    const res = await chatApi.initSession(context);
    if (typeof res === 'object' && res.suggested_questions?.length) {
      setSuggestedQuestions(res.suggested_questions);
    } else if (context.page_context !== 'recommended_plans') {
      setSuggestedQuestions([]);
    }
    return res;
  };

  return <AiContext.Provider value={{
    recommendations,
    suggestedType,
    recommendedPlans,
    highlights,
    route: recommendations?.route,
    updateRecommendations,
    loading,
    suggestedQuestions,
    initChatSession,
    chat: chatApi.chat
  }}>
    {children}
  </AiContext.Provider>
};

export const useAi = () => React.useContext(AiContext);
